import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowsLeftRight, Wrench, Trash, Truck, Package, HandArrowDown, PuzzlePiece, ClockCounterClockwise,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { DateField } from '@/components/ui/date-time-field';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LocationPicker } from '@/components/registration';
import { lifecycleApi } from './lifecycleApi';

const ICON = {
  issue: Package, lend: HandArrowDown, move: ArrowsLeftRight, install: PuzzlePiece,
  repair: Wrench, dispose: Trash, ship: Truck,
};

const STATUS_BADGE = {
  in_stock: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  in_use: 'bg-sky-50 text-sky-700 border-sky-200',
  repair: 'bg-amber-50 text-amber-700 border-amber-200',
  disposed: 'bg-zinc-100 text-zinc-500 border-zinc-200',
  shipped: 'bg-violet-50 text-violet-700 border-violet-200',
};

const HINT = {
  issue: '在庫から出します（数量を減らします）。',
  lend: '人に貸し出します。番号も場所も変わりません。',
  move: '持ち主の部署と場所だけ変えます。番号は変わりません（写しは作りません）。',
  install: '別の物に組み込みます。組込先が設備なら保全記録にも残ります。',
  repair: '修理中にします。戻すときは 移動 ではなく 貸出/出庫 で状態を戻します。',
  dispose: '廃棄します。理由は必須です。',
  ship: '出荷済にします。',
};

/**
 * <ItemLifecycleActions> — 全台帳で同じ操作（§8.5）。
 * 出庫 / 貸出 / 移動 / 組込 / 修理 / 廃棄 / 出荷。1操作＝履歴1行。
 *
 * register: eng | equip | mfg | bihin | it
 *
 * extraActions: 台帳にしかない操作（例: 技術資産の 返却）をこの帯の中に並べる。
 * 同じ操作が詳細画面の別の場所に二度出ないようにするため、台帳側のボタンは
 * ここへ渡して1か所にまとめる。
 */
export const ItemLifecycleActions = ({ register, itemId, onChanged, testId = 'lifecycle',
  extraActions = [], hideHistory = false, hideStatus = false, onlyActions = null }) => {
  const [state, setState] = useState(null);
  const [action, setAction] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [equip, setEquip] = useState([]);
  const [showLog, setShowLog] = useState(false);

  const load = useCallback(() => {
    if (!register || !itemId) return;
    lifecycleApi.state(register, itemId).then(setState).catch(() => setState(null));
  }, [register, itemId]);
  useEffect(() => { load(); }, [load]);

  const open = (a) => {
    setForm({ qty: state?.qty_tracked ? 1 : '', note: '', location_id: '', department_id: '',
      holder: '', due_date: '', target_id: '', target_label: '' });
    setAction(a);
    if (a === 'install' && equip.length === 0) lifecycleApi.equipment().then(setEquip);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await lifecycleApi.act(register, itemId, action, {
        qty: state?.qty_tracked && form.qty !== '' ? Number(form.qty) : null,
        note: form.note || null,
        location_id: form.location_id || null,
        department_id: form.department_id || null,
        holder: form.holder || null,
        due_date: form.due_date || null,
        target_id: form.target_id || null,
        target_label: form.target_label || null,
      });
      toast.success(`${state.actions.find((x) => x.action === action)?.label} を記録しました`,
        { description: (res.warnings || []).join(' / ') || undefined });
      setAction(null);
      load();
      onChanged?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '記録に失敗しました');
    } finally { setBusy(false); }
  };

  if (!state) return null;
  const tracked = state.qty_tracked;
  const label = (a) => state.actions.find((x) => x.action === a)?.label || a;

  const shownActions = onlyActions
    ? (state.actions || []).filter((a) => onlyActions.includes(a.action))
    : (state.actions || []);

  return (
    <div className={hideStatus ? 'contents' : 'rounded-xl border border-zinc-200 bg-white p-3'}
      data-testid={`${testId}-panel`}>
      {!hideStatus && (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-zinc-500">状態</span>
        <Badge variant="outline" className={`${STATUS_BADGE[state.status]} text-[11px]`}
          data-testid={`${testId}-status`}>{state.status_label}</Badge>
        {tracked && (
          <span className="text-[11px] text-zinc-500 tabular-nums" data-testid={`${testId}-qty`}>
            在庫 {state.qty ?? 0}{state.uom}
          </span>
        )}
        {(state.location_name || state.location_text) && (
          <span className="text-[11px] text-zinc-500" data-testid={`${testId}-location`}>
            {state.location_name || `未登録の場所: ${state.location_text}`}
          </span>
        )}
        {state.holder && (
          <span className="text-[11px] text-sky-700" data-testid={`${testId}-holder`}>
            貸出中: {state.holder}{state.due_date ? `（返却 ${state.due_date}）` : ''}
          </span>
        )}
        {!hideHistory && (
        <button type="button" onClick={() => setShowLog((v) => !v)}
          className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-teal-900"
          data-testid={`${testId}-history-toggle`}>
          <ClockCounterClockwise size={12} weight="bold" />
          履歴（{(state.history || []).length}）
        </button>
        )}
      </div>
      )}

      <div className={hideStatus ? 'contents' : 'mt-2.5 flex flex-wrap gap-1.5'}>
        {shownActions.map((a) => {
          const Icon = ICON[a.action];
          return (
            <Button key={a.action} size="sm" variant="outline" onClick={() => open(a.action)}
              className="h-8 text-xs" data-testid={`${testId}-action-${a.action}`}>
              <Icon size={13} className="mr-1" weight="bold" /> {a.label}
            </Button>
          );
        })}
        {extraActions.filter(Boolean).map((x) => (
          <Button key={x.key} size="sm" variant="outline" onClick={x.onClick}
            disabled={x.disabled} className={`h-8 text-xs ${x.className || ''}`}
            data-testid={x.testId || `${testId}-extra-${x.key}`}>
            {x.icon}{x.label}
          </Button>
        ))}
      </div>

      {showLog && !hideHistory && (
        <ul className="mt-3 divide-y divide-zinc-100 rounded-lg border border-zinc-200"
          data-testid={`${testId}-history`}>
          {(state.history || []).length === 0 && (
            <li className="px-3 py-2 text-[11px] text-zinc-400" data-testid={`${testId}-history-empty`}>
              まだ履歴はありません
            </li>
          )}
          {(state.history || []).map((h) => (
            <li key={h.id} className="flex flex-wrap items-baseline gap-x-2 px-3 py-1.5 text-[11px]"
              data-testid={`${testId}-history-row-${h.id}`}>
              <span className="font-semibold text-zinc-800">{h.action_label}</span>
              {h.qty ? <span className="tabular-nums text-zinc-600">{h.qty}{h.uom}</span> : null}
              {h.to_status_label && h.to_status !== h.from_status && (
                <span className="text-zinc-500">{h.from_status_label} → {h.to_status_label}</span>
              )}
              {h.location_name && <span className="text-zinc-500">{h.location_name}</span>}
              {h.holder && <span className="text-zinc-500">{h.holder}</span>}
              {h.target_label && <span className="text-zinc-500">→ {h.target_label}</span>}
              {h.note && <span className="text-zinc-500">{h.note}</span>}
              <span className="ml-auto text-zinc-400">{(h.at || '').slice(0, 16).replace('T', ' ')} · {h.by}</span>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="max-w-md" aria-describedby={undefined}
          data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>{action ? label(action) : ''} — {state.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[11px] leading-snug text-zinc-500" data-testid={`${testId}-dialog-hint`}>
              {HINT[action]}
            </p>

            {tracked && ['issue', 'install', 'ship', 'dispose'].includes(action) && (
              <div>
                <Label className="text-xs font-semibold text-zinc-600">
                  数量 ＊（在庫 {state.qty ?? 0}{state.uom}）
                </Label>
                <Input type="number" min="0" step="any" value={form.qty}
                  onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))}
                  className="h-10 mt-1 tabular-nums" data-testid={`${testId}-qty-input`} />
              </div>
            )}

            {action === 'move' && (
              <>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">移動先の場所</Label>
                  <LocationPicker value={form.location_id} legacyText={state.location_text || ''}
                    onChange={(id) => setForm((f) => ({ ...f, location_id: id }))}
                    testId={`${testId}-location-picker`} />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">移動先の部署</Label>
                  <select value={form.department_id}
                    onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
                    className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                    data-testid={`${testId}-department`}>
                    <option value="">変えない</option>
                    {(state.departments || []).map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {action === 'lend' && (
              <>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">貸出先（人・部署）</Label>
                  <Input value={form.holder}
                    onChange={(e) => setForm((f) => ({ ...f, holder: e.target.value }))}
                    className="h-10 mt-1" data-testid={`${testId}-holder-input`} />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">返却期限</Label>
                  <DateField value={form.due_date}
                    onChange={(v) => setForm((f) => ({ ...f, due_date: v }))}
                    className="mt-1" inputClassName="h-10" testId={`${testId}-due-date`} />
                </div>
              </>
            )}

            {action === 'install' && (
              <div>
                <Label className="text-xs font-semibold text-zinc-600">組込先の設備</Label>
                <select value={form.target_id}
                  onChange={(e) => {
                    const row = equip.find((x) => x.id === e.target.value);
                    setForm((f) => ({ ...f, target_id: e.target.value, target_label: row?.name || '' }));
                  }}
                  className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                  data-testid={`${testId}-target`}>
                  <option value="">（選ばない）</option>
                  {equip.map((x) => (
                    <option key={x.id} value={x.id}>{x.asset_no ? `${x.asset_no} ` : ''}{x.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold text-zinc-600">
                メモ{action === 'dispose' ? ' ＊（理由）' : ''}
              </Label>
              <Textarea rows={2} value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                className="mt-1" data-testid={`${testId}-note`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)}
              data-testid={`${testId}-cancel`}>キャンセル</Button>
            <Button onClick={submit} disabled={busy}
              className={action === 'dispose' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-teal-700 hover:bg-teal-800'}
              data-testid={`${testId}-submit`}>
              {busy ? '記録中…' : '記録する'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ItemLifecycleActions;
